import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { FileText, Calendar, Download, FileDown, Paperclip, CheckCircle, TrendingUp, ClipboardList } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const INTERP_COLOR = { green: 'bg-[#2E8B57]/10 text-[#2E8B57]', yellow: 'bg-[#F5B041]/10 text-[#F5B041]', orange: 'bg-[#E67E22]/10 text-[#E67E22]', red: 'bg-[#D3544E]/10 text-[#D3544E]', darkred: 'bg-[#922B21]/10 text-[#922B21]' };

export default function ProgressPage() {
  const [notes, setNotes] = useState([]);
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/notes'),
      api.get('/questionnaires/assignments/list'),
    ]).then(([nRes, aRes]) => {
      setNotes(nRes.data);
      setAssignments(aRes.data);
    }).catch(() => {});
  }, []);

  const downloadAttachment = async (noteId, attachmentId, filename) => {
    try {
      const res = await api.get(`/notes/${noteId}/attachments/${attachmentId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  const downloadReport = async (noteId) => {
    try {
      const res = await api.get(`/notes/${noteId}/report`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `clinical_report_${noteId.slice(0,8)}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  // Group completed assignments by questionnaire name
  const completed = assignments.filter(a => a.status === 'COMPLETED');
  const grouped = {};
  completed.forEach(a => {
    const name = a.questionnaire_title || 'Unknown';
    if (!grouped[name]) grouped[name] = [];
    grouped[name].push(a);
  });
  // Sort each group chronologically
  Object.values(grouped).forEach(arr => arr.sort((a, b) => (a.completed_at || '').localeCompare(b.completed_at || '')));

  const hasAssessments = completed.length > 0;

  return (
    <div className="space-y-6" data-testid="progress-page">
      <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Therapy Progress</h1>

      {/* Assessment Progress Section */}
      {hasAssessments && (
        <div className="space-y-4" data-testid="assessment-progress-section">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#0D8B7D]" />
            <h2 className="text-lg font-semibold text-[#1A3330] font-['Outfit']">Assessment Progress</h2>
          </div>

          {Object.entries(grouped).map(([qName, items]) => {
            const isPublic = items[0]?.score_visibility !== 'PRIVATE';
            const hasMultiple = items.length > 1 && isPublic;
            const chartData = hasMultiple ? items.map(a => ({
              date: a.completed_at ? new Date(a.completed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : '',
              score: a.score,
            })) : [];

            return (
              <Card key={qName} className="bg-white border-[#E5F2F1] rounded-2xl" data-testid={`assessment-group-${qName.replace(/\s+/g, '-')}`}>
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-[#1A3330] font-['Outfit'] mb-3">{qName}</h3>

                  {/* Progress chart for repeated public assessments */}
                  {hasMultiple && (
                    <div className="mb-4" data-testid={`assessment-chart-${qName.replace(/\s+/g, '-')}`}>
                      <ResponsiveContainer width="100%" height={160}>
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5F2F1" />
                          <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#4A6B68' }} />
                          <YAxis tick={{ fontSize: 10, fill: '#4A6B68' }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="score" stroke="#0D8B7D" strokeWidth={2} dot={{ fill: '#0D8B7D', r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Assessment entries */}
                  <div className="space-y-2">
                    {items.map((a, idx) => (
                      <div key={a._id} className="flex items-center justify-between py-2 border-b border-[#E5F2F1] last:border-0" data-testid={`assessment-entry-${a._id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-[#0D8B7D]/8 flex items-center justify-center">
                            <CheckCircle className="w-3.5 h-3.5 text-[#0D8B7D]" />
                          </div>
                          <div>
                            <p className="text-xs text-[#4A6B68]">
                              {a.completed_at ? new Date(a.completed_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : a.assigned_date}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isPublic ? (
                            <>
                              <span className="text-sm font-semibold text-[#0D8B7D]">{a.score}</span>
                              {a.interpretation && (
                                <Badge className={`text-[10px] border-0 ${INTERP_COLOR[a.interpretation_color] || 'bg-[#0D8B7D]/10 text-[#0D8B7D]'}`}>
                                  {a.interpretation}
                                </Badge>
                              )}
                            </>
                          ) : (
                            <span className="text-xs text-[#4A6B68] italic">Score available to your therapist</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Session Notes Section */}
      <div>
        <p className="text-sm text-[#4A6B68] mb-4">Session notes shared by your therapist</p>
        <div className="space-y-4">
          {notes.map(n => (
            <Card key={n._id} className="bg-white border-[#E5F2F1] rounded-2xl" data-testid={`progress-note-${n._id}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[#0D8B7D]/10 flex items-center justify-center"><FileText className="w-4 h-4 text-[#0D8B7D]" /></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#1A3330]">{n.session_title || 'Session Note'}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-[#4A6B68] flex items-center gap-1"><Calendar className="w-3 h-3" />{n.session_date} · {n.therapist_name}</p>
                      {n.last_edited_at && (
                        <span className="text-[10px] text-[#F5B041] italic">
                          Edited {new Date(n.last_edited_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className="bg-[#2E8B57]/10 text-[#2E8B57] text-[10px] border-0">Shared</Badge>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[#0D8B7D] text-[10px]" onClick={() => downloadReport(n._id)}>
                      <FileDown className="w-3.5 h-3.5 mr-1" />Report
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-[#1A3330] whitespace-pre-wrap leading-relaxed">{n.content}</p>
                {n.attachments && n.attachments.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#E5F2F1]">
                    <p className="text-[11px] text-[#4A6B68] uppercase tracking-wider mb-2 flex items-center gap-1"><Paperclip className="w-3 h-3" />Attachments</p>
                    <div className="space-y-1.5">
                      {n.attachments.map(att => (
                        <div key={att.id} className="flex items-center justify-between bg-[#F4FAF9] rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2">
                            <FileText className="w-3.5 h-3.5 text-[#0D8B7D]" />
                            <span className="text-xs text-[#1A3330]">{att.original_filename}</span>
                            <span className="text-[10px] text-[#4A6B68]">({(att.size / 1024).toFixed(1)} KB)</span>
                          </div>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => downloadAttachment(n._id, att.id, att.original_filename)}>
                            <Download className="w-3.5 h-3.5 text-[#0D8B7D]" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {notes.length === 0 && !hasAssessments && <p className="text-sm text-[#4A6B68]">No shared notes yet. Your therapist will share session notes here.</p>}
        </div>
      </div>
    </div>
  );
}
